import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ValidationIssue } from "@/context/CampaignBuilderContext";

interface ValidationToastProps {
  open: boolean;
  issues: ValidationIssue[];
  onClose: () => void;
  /** Header line above the issue list. */
  title?: string;
}

/**
 * "Please complete before you continue" validation toast — a persistent Good Wine
 * (error) alert anchored top-right beneath the Continue button, sliding down from
 * above. Lists each unmet requirement as "Tab: message" and offers a single FIX NOW
 * that jumps to the first fixable issue. Ported 1:1 from the prototype's
 * `.validation-toast`. Portaled + reusable across steps. Dismiss via ✕ or FIX NOW.
 */
export default function ValidationToast({
  open,
  issues,
  onClose,
  title = "Please complete before you continue:",
}: ValidationToastProps) {
  // "visible" drives the slide transition; "mounted" keeps the node during exit.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  // Freeze the issues while open so the slide-out still shows them after the parent
  // clears the list (mirrors CampaignBuilderModal's stable snapshot).
  const [shown, setShown] = useState<ValidationIssue[]>(issues);

  useEffect(() => {
    if (open && issues.length > 0) {
      setShown(issues);
      setMounted(true);
      // Paint the off-screen state before transitioning in.
      const id = setTimeout(() => setVisible(true), 16);
      return () => clearTimeout(id);
    }
    setVisible(false);
  }, [open, issues]);

  if (!mounted) return null;

  const firstFix = shown.find((i) => i.onFix)?.onFix;

  return createPortal(
    <div
      role="alert"
      onTransitionEnd={(e) => {
        if (e.propertyName === "top" && !visible) setMounted(false);
      }}
      className="fixed right-8 z-10000 w-90 max-w-[calc(100vw-64px)] font-lato"
      style={{
        top: visible ? 80 : -200,
        background: "var(--color-good-wine-bg)",
        border: "1px solid var(--color-good-wine)",
        borderLeft: "4px solid var(--color-good-wine)",
        borderRadius: "var(--radius-card)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        padding: "16px 20px",
        transition: "top 0.4s cubic-bezier(0.22, 0.61, 0.36, 1)",
      }}
    >
      {/* Header — error icon + title + dismiss */}
      <div className="mb-2.5 flex items-center gap-2">
        <ErrorOutlineIcon sx={{ fontSize: 20, color: "var(--color-good-wine)" }} />
        <span className="text-[14px] font-bold tracking-[0.2px] text-rodeo-black">{title}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="ml-auto flex cursor-pointer items-center border-none bg-transparent p-0 text-graphite transition-colors hover:text-rodeo-black"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      {/* Issues — "Tab: message" lines */}
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {shown.map((issue) => (
          <li
            key={`${issue.tab}:${issue.message}`}
            className="text-[14px] leading-normal text-graphite"
          >
            <span className="font-bold text-rodeo-black">{issue.tab}:</span> {issue.message}
          </li>
        ))}
      </ul>

      {/* Recovery action — routes to the first fixable issue */}
      {firstFix && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() => {
              firstFix();
              onClose();
            }}
            className="cursor-pointer rounded-sm border-none bg-rodeo-black px-4 py-2 text-[12px] font-bold tracking-[0.6px] text-white transition-colors hover:bg-[#1a1a1a]"
          >
            FIX NOW
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}
